import { useCallback } from "react";
import { useSwivuBryntumContext } from "../useSwivuBryntumContext";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { generateInsight, getInsightTypeAvatar, cancelInsightGeneration } from "@/network/network";
import { Insights } from "@/bryntum-lib/models/Insights";

interface GenerateInsightOptions {
  file_id?: number | null;
  insight_type: string;
  language?: string;
  signal?: AbortSignal;
  company_cam_url?: string;
  unassigned_file_id?: number;
  board_id?: number;
}

export const useFileInsights = () => {
  const { project, selectedBoard } = useSwivuBryntumContext();

  /**
   * Delete an insight by ID
   * @param id - The insight ID to delete
   */
  const deleteInsight = useCallback(
    (id: string | number) => {
      if (!project) {
        console.warn("[useFileInsights] Project not available");
        return;
      }

      const store = project.getCrudStore(STORE_KEYS.insightsStore);
      if (!store) {
        console.warn(`[useFileInsights] Store with id "${STORE_KEYS.insightsStore}" not found`);
        return;
      }

      store.remove(id);
    },
    [project]
  );

  /**
   * Generate a new insight for a file
   * @param file_id - The file ID to generate insight for (pass null for CC images)
   * @param insight_type - The type of insight (analyze, execute, review, site_safety, workmanship)
   * @param language - The language code (en or es, defaults to en)
   * @param signal - Optional AbortSignal to cancel the request
   * @param company_cam_url - CC image URL; when provided, sent instead of file_id
   * @returns Promise that resolves when insight is added to store
   */
  const generateInsightForFile = useCallback(
    async (file_id: number | null, insight_type: string, language: string = "en", signal?: AbortSignal, company_cam_url?: string) => {
      if (!project) {
        console.warn("[useFileInsights] Project not available");
        return;
      }

      const body: Record<string, unknown> = { insight_type, language };
      if (selectedBoard?.id) {
        body.board_id = selectedBoard.id;
      }
      if (company_cam_url) {
        body.company_cam_url = company_cam_url;
      } else {
        body.file_id = file_id;
      }

      try {
        const response = await generateInsight(body, signal);

        const store = project.getCrudStore(STORE_KEYS.insightsStore);
        if (!store) {
          console.warn(`[useFileInsights] Store with id "${STORE_KEYS.insightsStore}" not found`);
          return;
        }

        // Add the insight to the store
        store.add(response.data);
      } catch (error) {
        console.error("[useFileInsights] Failed to generate insight:", error);
        throw error;
      }
    },
    [project, selectedBoard]
  );

  /**
   * Generate insight for an unassigned file using the user's default board preference.
   */
  const generateInsightForUnassignedFile = useCallback(
    async ({ unassigned_file_id, insight_type, language = "en", signal, board_id }: GenerateInsightOptions) => {
      const body: Record<string, unknown> = {
        unassigned_file_id,
        insight_type,
        language,
        board_id,
      };

      try {
        const response = await generateInsight(body, signal);
        return response.data;
      } catch (error) {
        console.error("[useFileInsights] Failed to generate insight for unassigned file:", error);
        throw error;
      }
    },
    []
  );

  /**
   * Get avatar URL for an insight type
   * @param insightType - The insight type (analyze, execute, review, site_safety, workmanship)
   * @returns Promise that resolves with avatar_url
   */
  const getInsightTypesAvatar = useCallback(async (insightType: string): Promise<string | null> => {
    try {
      const response = await getInsightTypeAvatar(insightType);
      return response.data?.avatar_url || null;
    } catch (error) {
      console.error(`[useFileInsights] Failed to get avatar for insight type "${insightType}":`, error);
      return null;
    }
  }, []);

  /**
   * Get all insights for a specific file or CC image
   * @param fileId - The file ID to get insights for (ignored when companyCamUrl is provided)
   * @param companyCamUrl - CC image URL; when provided, insights are queried by company_cam_url
   * @returns Array of insights for the file or CC image
   */
  const getInsights = useCallback(
    (fileId: number | string | undefined, companyCamUrl?: string): Insights[] => {
      if (!project || (!fileId && !companyCamUrl)) {
        return [];
      }

      const store = project.getCrudStore(STORE_KEYS.insightsStore);
      if (!store) {
        console.warn(`[useFileInsights] Store with id "${STORE_KEYS.insightsStore}" not found`);
        return [];
      }

      if (companyCamUrl) {
        return store.query((insight: Insights) => insight.companyCamUrl === companyCamUrl) as Insights[];
      }

      return store.query((insight: Insights) => insight.file === fileId) as Insights[];
    },
    [project]
  );

  /**
   * Cancel insight generation for a file or CC image
   * @param file_id - The file ID (ignored when company_cam_url is provided)
   * @param insight_type - The type of insight being cancelled
   * @param company_cam_url - CC image URL; when provided, sent instead of file_id
   * @returns Promise that resolves when cancellation is complete
   */
  const cancelInsight = useCallback(
    async (file_id: number, insight_type: string, company_cam_url?: string) => {
      const body: Record<string, unknown> = { insight_type };
      if (selectedBoard?.id) {
        body.board_id = selectedBoard.id;
      }
      if (company_cam_url) {
        body.company_cam_url = company_cam_url;
      } else {
        body.file_id = file_id;
      }
      try {
        await cancelInsightGeneration(body);
      } catch (error) {
        console.error("[useFileInsights] Failed to cancel insight generation:", error);
      }
    },
    [selectedBoard]
  );

  /**
   * Cancel insight generation for an unassigned file (no board_id needed).
   */
  const cancelInsightForUnassignedFile = useCallback(
    async (unassigned_file_id: number, insight_type: string) => {
      const body: Record<string, unknown> = { unassigned_file_id, insight_type };
      try {
        await cancelInsightGeneration(body);
      } catch (error) {
        console.error("[useFileInsights] Failed to cancel insight generation for unassigned file:", error);
      }
    },
    []
  );

  return {
    deleteInsight,
    generateInsight: generateInsightForFile,
    generateInsightForUnassignedFile,
    getInsightTypesAvatar,
    getInsights,
    cancelInsight,
    cancelInsightForUnassignedFile,
  };
};
